import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPaymentIntent } from '../services/registrationApi';
import { getApiBaseUrl } from '../services/apiConfig';
import { formatCurrency } from '../utils/pricing';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const cardStyle = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1f2a37',
      fontFamily: 'Source Sans 3, Segoe UI, sans-serif',
      '::placeholder': { color: '#8a97a8' },
    },
    invalid: { color: '#c0392b' },
  },
};

function StripeCardFields({ amountCents, testCount, studentCount, metadata, apiRef }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState('');

  useImperativeHandle(apiRef, () => ({
    async confirmPayment() {
      setError('');

      if (!stripe || !elements) {
        return {
          ok: false,
          error: 'Stripe has not finished loading. Please try again.',
        };
      }

      const card = elements.getElement(CardElement);
      if (!card) {
        return { ok: false, error: 'Card fields are not available.' };
      }

      try {
        const intent = await createPaymentIntent({
          amountCents,
          testCount,
          studentCount,
          metadata,
        });

        if (
          intent.demo ||
          !intent.clientSecret ||
          String(intent.clientSecret).includes('_secret_demo')
        ) {
          return {
            ok: true,
            status: 'demo_authorized',
            paymentIntentId: intent.paymentIntentId,
            amountCents,
            note: 'Demo PaymentIntent — configure STRIPE_SECRET_KEY on the API for live charges.',
          };
        }

        const { error: confirmError, paymentIntent } =
          await stripe.confirmCardPayment(intent.clientSecret, {
            payment_method: { card },
          });

        if (confirmError) {
          setError(confirmError.message);
          return { ok: false, error: confirmError.message };
        }

        // The server only authorizes the card (the money is captured after the
        // registration is saved), so 'requires_capture' is the success state.
        // 'succeeded' is still accepted for a server that captures immediately.
        if (!['requires_capture', 'succeeded'].includes(paymentIntent?.status)) {
          const message = `Payment status: ${paymentIntent?.status || 'unknown'}`;
          setError(message);
          return { ok: false, error: message };
        }

        return {
          ok: true,
          status: paymentIntent.status,
          paymentIntentId: paymentIntent.id,
          amountCents: paymentIntent.amount,
        };
      } catch (err) {
        const message = err.message || 'Payment failed.';
        setError(message);
        return { ok: false, error: message };
      }
    },
  }));

  return (
    <div className="stripe-panel">
      <div className="stripe-brand">
        <span className="stripe-logo">stripe</span>
        <span>Payment Method</span>
      </div>
      <div className="card-element-wrap">
        <CardElement options={cardStyle} onChange={() => setError('')} />
      </div>
      {error ? <p className="field-error">{error}</p> : null}
      <p className="stripe-note">
        Card details are processed by Stripe.
      </p>
    </div>
  );
}

/**
 * Shown when REACT_APP_STRIPE_PUBLISHABLE_KEY is not set.
 * Does not require card entry — Register continues with a demo payment
 * so Zoho CRM sync can still be tested.
 */
function DemoPaymentNotice({ amountCents, testCount, studentCount, metadata, apiRef }) {
  const [error, setError] = useState('');
  const hasApi = Boolean(getApiBaseUrl());

  useImperativeHandle(apiRef, () => ({
    async confirmPayment() {
      setError('');
      try {
        if (hasApi) {
          const intent = await createPaymentIntent({
            amountCents,
            testCount,
            studentCount,
            metadata,
          });
          return {
            ok: true,
            status: 'demo_authorized',
            paymentIntentId: intent.paymentIntentId,
            amountCents,
            note: 'Demo payment — Stripe publishable key not configured.',
          };
        }

        return {
          ok: true,
          status: 'demo_authorized',
          paymentIntentId: `demo_${Date.now()}`,
          amountCents,
          note: 'Local demo payment — API base URL not configured.',
        };
      } catch (err) {
        const message = err.message || 'Payment failed.';
        setError(message);
        return { ok: false, error: message };
      }
    },
  }));

  return (
    <div className="stripe-panel">
      <div className="stripe-brand">
        <span className="stripe-logo">stripe</span>
        <span>Payment Method</span>
      </div>
      <p className="demo-payment-amount">
        Amount due: <strong>{formatCurrency(amountCents / 100)}</strong>
      </p>
      <p className="stripe-note">
        Stripe is not configured yet (<code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code>
        ). Click <strong>Register</strong> to continue without charging a card —
        the school record can still be created in Zoho CRM.
      </p>
      {error ? <p className="field-error">{error}</p> : null}
    </div>
  );
}

/**
 * Pay-by-check path for pre-approved schools. The entered code is validated
 * server-side at submit time — this component only collects it.
 */
function CheckPayment({ amountCents, apiRef, onCancel }) {
  const [code, setCode] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [error, setError] = useState('');
  const valuesRef = useRef({ code: '', checkNumber: '' });
  valuesRef.current = { code, checkNumber };

  useImperativeHandle(apiRef, () => ({
    async confirmPayment() {
      setError('');
      const enteredCode = valuesRef.current.code.trim();
      const enteredCheck = valuesRef.current.checkNumber.trim();
      if (!enteredCode) {
        const message = 'Enter your authorization code to register by check.';
        setError(message);
        return { ok: false, error: message };
      }
      if (!enteredCheck) {
        const message = 'Enter the check number to register by check.';
        setError(message);
        return { ok: false, error: message };
      }
      return {
        ok: true,
        method: 'check',
        status: 'pending_check',
        accessCode: enteredCode,
        checkNumber: enteredCheck,
        paymentIntentId: null,
        amountCents,
      };
    },
  }));

  return (
    <div className="stripe-panel">
      <div className="stripe-brand">
        <span>Pay by Check</span>
      </div>
      <p className="demo-payment-amount">
        Amount due: <strong>{formatCurrency(amountCents / 100)}</strong>
      </p>
      <div className="check-fields">
        <div className="card-element-wrap">
          <input
            type="text"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              setError('');
            }}
            placeholder="Authorization code"
            autoComplete="off"
            aria-label="Pay-by-check authorization code"
          />
        </div>
        <div className="card-element-wrap">
          <input
            type="text"
            value={checkNumber}
            onChange={(e) => {
              setCheckNumber(e.target.value);
              setError('');
            }}
            placeholder="Check number"
            autoComplete="off"
            aria-label="Check number"
          />
        </div>
      </div>
      <p className="stripe-note">
        Enter the authorization code and your check number, then click{' '}
        <strong>Register</strong>. Your registration is recorded as{' '}
        <strong>payment pending</strong> and marked paid once your check is
        received and verified.
      </p>
      {error ? <p className="field-error">{error}</p> : null}
      <button type="button" className="btn-link" onClick={onCancel}>
        Pay by card instead
      </button>
    </div>
  );
}

export const StripePayment = forwardRef(function StripePayment(
  { amountCents, testCount, studentCount, metadata = {} },
  ref,
) {
  const innerRef = useRef(null);
  const useLiveStripe = useMemo(() => Boolean(stripePromise), []);
  const [payByCheck, setPayByCheck] = useState(false);

  useImperativeHandle(ref, () => ({
    confirmPayment: () => {
      if (!innerRef.current?.confirmPayment) {
        return Promise.resolve({
          ok: false,
          error: 'Payment form is not ready.',
        });
      }
      return innerRef.current.confirmPayment();
    },
  }));

  if (payByCheck) {
    return (
      <CheckPayment
        apiRef={innerRef}
        amountCents={amountCents}
        onCancel={() => setPayByCheck(false)}
      />
    );
  }

  return (
    <>
      {useLiveStripe ? (
        <Elements stripe={stripePromise}>
          <StripeCardFields
            apiRef={innerRef}
            amountCents={amountCents}
            testCount={testCount}
            studentCount={studentCount}
            metadata={metadata}
          />
        </Elements>
      ) : (
        <DemoPaymentNotice
          apiRef={innerRef}
          amountCents={amountCents}
          testCount={testCount}
          studentCount={studentCount}
          metadata={metadata}
        />
      )}
      <button
        type="button"
        className="btn-link pay-by-check-link"
        onClick={() => setPayByCheck(true)}
      >
        Registering with a pay-by-check authorization code?
      </button>
    </>
  );
});
